/**
 * Tickets API Service
 * Handles ticket-related API calls
 */

import { get } from "./client";
import { TicketOption } from "../../types";

interface ApiTicket {
  id: string;
  name: string;
  slug: string;
  price: number;
  original_price: number | null;
  type: "regular" | "fast-track";
  description: string | null;
  ticket_features: string[] | string | null;
  is_recommended: boolean;
}

interface TicketsResponse {
  success: boolean;
  data: ApiTicket[];
}

interface TicketResponse {
  success: boolean;
  data: ApiTicket;
}

/**
 * Transform API ticket to frontend TicketOption format
 */
function transformTicket(apiTicket: ApiTicket): TicketOption {
  let features: string[] = [];

  if (apiTicket.ticket_features) {
    if (typeof apiTicket.ticket_features === "string") {
      try {
        features = JSON.parse(apiTicket.ticket_features);
      } catch {
        features = [apiTicket.ticket_features];
      }
    } else if (Array.isArray(apiTicket.ticket_features)) {
      features = apiTicket.ticket_features;
    }
  }

  return {
    id: apiTicket.id,
    title: apiTicket.name,
    description: apiTicket.description || "",
    price: apiTicket.price,
    originalPrice: apiTicket.original_price ?? undefined,
    features,
    recommended: apiTicket.is_recommended,
    type: apiTicket.type,
  };
}

/**
 * Fetch all available tickets
 */
export async function getTickets(): Promise<TicketOption[]> {
  const response = await get<TicketsResponse>(
    "/public/tickets",
    undefined,
    undefined,
    false
  );
  return response.data.map(transformTicket);
}

/**
 * Fetch single ticket by slug
 */
export async function getTicketBySlug(slug: string): Promise<TicketOption> {
  const response = await get<TicketResponse>(
    `/public/tickets/${slug}`,
    undefined,
    undefined,
    false
  );
  return transformTicket(response.data);
}

export interface ServiceFeeInfo {
  isInFreePeriod: boolean;
  serviceFeePercent: number;
  baseGroupDiscount: number;
  effectiveGroupDiscount: number;
}

interface ServiceFeeInfoResponse {
  success: boolean;
  data: ServiceFeeInfo;
}

/**
 * Fetch service fee info for group discount calculation
 */
export async function getServiceFeeInfo(): Promise<ServiceFeeInfo> {
  const response = await get<ServiceFeeInfoResponse>(
    "/public/tickets/service-fee-info",
    undefined,
    undefined,
    false
  );
  return response.data;
}
